/**
 * Pure data helpers for <g-tree>. No DOM in here, so it can be tested under
 * node:test like the table renderers.
 *
 * The tree reads two shapes and writes back in whichever one it was given:
 *
 *   nested — each record carries its children in an array (`fields.children`)
 *   flat   — one array, each record names its parent (`fields.parent`).
 *            This is the shape a Gainable dataset actually has.
 *
 * Every mutation edits the HOST's own array and records — the same bargain
 * <g-task-list> strikes — so the host's job is to persist, not to re-apply.
 */

import { compareText } from '../util/collate.js';

export const DEFAULT_FIELDS = {
  id: 'id',
  label: null,        // null = first of name / label / title the record has
  icon: 'icon',
  children: 'children',
  parent: null,       // set → flat mode
  type: 'type',
};

/** `config.fields` merged over the defaults. */
export function resolveFields(config) {
  return { ...DEFAULT_FIELDS, ...((config && config.fields) || {}) };
}

export function labelOf(record, f) {
  if (!record) return '';
  const v = f.label ? record[f.label] : (record.name ?? record.label ?? record.title);
  return v == null ? '' : String(v);
}

/**
 * Builds the view model.
 *
 * @param {Array} data
 * @param {Object} opts
 * @param {Object} opts.fields      resolved fields
 * @param {(record) => string} opts.idOf
 * @param {(record) => boolean} [opts.isFolder]  host override
 * @param {string} [opts.folderType]  value of `fields.type` that marks a folder (default 'folder')
 * @returns {{ roots: Node[], byId: Map<string, Node>, flat: boolean }}
 *
 * Node = { id, record, parent: Node|null, children: Node[], depth, folder }
 */
export function buildTree(data, opts) {
  const { fields: f, idOf } = opts;
  const flat = !!f.parent;
  const byId = new Map();
  const roots = [];
  const list = Array.isArray(data) ? data : [];

  const make = (record, parent, depth) => {
    const node = { id: idOf(record), record, parent, children: [], depth, folder: false };
    byId.set(node.id, node);
    return node;
  };

  if (flat) {
    const nodes = list.filter(r => r && typeof r === 'object').map(r => make(r, null, 0));
    for (const node of nodes) {
      const pid = node.record[f.parent];
      const parent = pid == null || pid === '' ? null : byId.get(String(pid));
      // An orphan (parent not in the list) or a self-reference is shown at the
      // root rather than dropped — losing a file from view is worse than
      // showing it one level too high.
      if (parent && parent !== node && !isAncestor(node, parent)) {
        node.parent = parent;
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
    const setDepth = (n, d) => { n.depth = d; n.children.forEach(c => setDepth(c, d + 1)); };
    roots.forEach(n => setDepth(n, 0));
  } else {
    const seen = new Set();
    const walk = (records, parent, depth, into) => {
      for (const record of records) {
        // A record that appears twice (or contains itself) would recurse forever.
        if (!record || typeof record !== 'object' || seen.has(record)) continue;
        seen.add(record);
        const node = make(record, parent, depth);
        into.push(node);
        const kids = record[f.children];
        if (Array.isArray(kids)) walk(kids, node, depth + 1, node.children);
      }
    };
    walk(list, null, 0, roots);
  }

  const folderType = opts.folderType ?? 'folder';
  for (const node of byId.values()) {
    node.folder = folderOf(node, f, opts.isFolder, folderType, flat);
  }
  return { roots, byId, flat };
}

function folderOf(node, f, isFolder, folderType, flat) {
  const r = node.record;
  if (typeof isFolder === 'function') {
    try { return !!isFolder(r); } catch { /* fall through */ }
  }
  if (f.type && r[f.type] != null && r[f.type] !== '') return r[f.type] === folderType;
  if (!flat && Array.isArray(r[f.children])) return true;
  return node.children.length > 0;
}

/** True when `maybe` is `node` itself or one of its ancestors. */
export function isAncestor(maybe, node) {
  for (let n = node; n; n = n.parent) if (n === maybe) return true;
  return false;
}

/** Every node below `node`, depth first. */
export function descendants(node) {
  const out = [];
  const walk = (n) => n.children.forEach(c => { out.push(c); walk(c); });
  walk(node);
  return out;
}

/** Root → node, inclusive. */
export function pathTo(node) {
  const out = [];
  for (let n = node; n; n = n.parent) out.unshift(n);
  return out;
}

/**
 * Siblings in display order. `sort`: 'manual' (data order), 'name', or
 * 'folders-first' (folders, then files, each by name — a file browser).
 */
export function sortNodes(nodes, sort, f) {
  if (sort !== 'name' && sort !== 'folders-first') return nodes;
  const cmp = (a, b) => compareText(labelOf(a.record, f), labelOf(b.record, f));
  return [...nodes].sort((a, b) => {
    if (sort === 'folders-first' && a.folder !== b.folder) return a.folder ? -1 : 1;
    return cmp(a, b);
  });
}

// ── Mutations (host data, in place) ──

/** The array that holds `parent`'s children in nested mode, created on demand. */
function childArray(data, parentRecord, f) {
  if (!parentRecord) return data;
  if (!Array.isArray(parentRecord[f.children])) parentRecord[f.children] = [];
  return parentRecord[f.children];
}

/**
 * Adds `record` as the last child of `parent` (null = root).
 * In flat mode the parent link is written onto the record.
 */
export function insertRecord(data, tree, record, parent, f) {
  if (tree.flat) {
    record[f.parent] = parent ? parent.record[f.id] ?? parent.record._id ?? parent.id : null;
    data.push(record);
  } else {
    childArray(data, parent && parent.record, f).push(record);
  }
}

/**
 * Removes `node` and everything below it. Returns the removed records,
 * the node's own first.
 */
export function removeNode(data, tree, node, f) {
  const removed = [node.record, ...descendants(node).map(n => n.record)];
  if (tree.flat) {
    const gone = new Set(removed);
    for (let i = data.length - 1; i >= 0; i--) if (gone.has(data[i])) data.splice(i, 1);
  } else {
    const arr = node.parent ? node.parent.record[f.children] : data;
    const i = arr.indexOf(node.record);
    if (i >= 0) arr.splice(i, 1);
  }
  return removed;
}

/**
 * Moves `node` under `newParent` (null = root) so that it lands at `index`
 * among the new parent's children (the moved node excluded when counting).
 * Refuses a move into the node's own subtree.
 *
 * @returns {boolean} whether anything moved
 */
export function moveNode(data, tree, node, newParent, index, f) {
  if (newParent && isAncestor(node, newParent)) return false;
  const siblings = (newParent ? newParent.children : tree.roots).filter(n => n !== node);
  const clamped = Math.max(0, Math.min(index, siblings.length));

  if (tree.flat) {
    const i = data.indexOf(node.record);
    if (i < 0) return false;
    data.splice(i, 1);
    node.record[f.parent] = newParent ? (newParent.record[f.id] ?? newParent.record._id ?? newParent.id) : null;
    // Siblings are ordered by array position, so land next to the sibling we
    // are meant to precede (or follow the last one). Everything else in the
    // array keeps its place.
    let at;
    if (clamped < siblings.length) at = data.indexOf(siblings[clamped].record);
    else if (siblings.length) at = data.indexOf(siblings[siblings.length - 1].record) + 1;
    else at = data.length;
    data.splice(at < 0 ? data.length : at, 0, node.record);
  } else {
    const from = node.parent ? node.parent.record[f.children] : data;
    const i = from.indexOf(node.record);
    if (i < 0) return false;
    from.splice(i, 1);
    const to = childArray(data, newParent && newParent.record, f);
    // Recount against the destination array: it is the source array when the
    // move stays within one parent, now one shorter.
    const before = siblings[clamped];
    const at = before ? to.indexOf(before.record) : to.length;
    to.splice(at < 0 ? to.length : at, 0, node.record);
  }
  return true;
}

// ── File icons ──
//
// Font Awesome, like every icon in this stack. Tinted with semantic tokens only
// so a theme switch keeps them legible.

const EXT_ICONS = [
  [['pdf'], 'fa-regular fa-file-pdf', 'text-error'],
  [['doc', 'docx', 'odt', 'rtf', 'pages'], 'fa-regular fa-file-word', 'text-info'],
  [['xls', 'xlsx', 'ods', 'numbers'], 'fa-regular fa-file-excel', 'text-success'],
  [['csv', 'tsv'], 'fa-regular fa-file-csv', 'text-success'],
  [['ppt', 'pptx', 'odp', 'key'], 'fa-regular fa-file-powerpoint', 'text-warning'],
  [['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'heic', 'bmp', 'tif', 'tiff'], 'fa-regular fa-file-image', 'text-secondary'],
  [['mp4', 'mov', 'avi', 'mkv', 'webm'], 'fa-regular fa-file-video', 'text-accent'],
  [['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'], 'fa-regular fa-file-audio', 'text-accent'],
  [['zip', 'rar', '7z', 'tar', 'gz'], 'fa-regular fa-file-zipper', ''],
  [['js', 'ts', 'json', 'html', 'css', 'py', 'rb', 'go', 'java', 'xml', 'yml', 'yaml', 'sql'], 'fa-regular fa-file-code', ''],
  [['txt', 'md', 'log'], 'fa-regular fa-file-lines', ''],
];
const EXT_MAP = new Map();
for (const [exts, icon, tone] of EXT_ICONS) for (const e of exts) EXT_MAP.set(e, { icon, tone });

/** `'Q3 Report.PDF'` → `{ icon: 'fa-regular fa-file-pdf', tone: 'text-error' }`, or null. */
export function fileIconFor(name) {
  const m = /\.([a-z0-9]{1,8})$/i.exec(String(name || ''));
  return (m && EXT_MAP.get(m[1].toLowerCase())) || null;
}
